import React, { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { UseFormSetValue, UseFormTrigger } from 'react-hook-form';

export interface CandidateFormData {
  name: string;
  email: string;
  phone?: string;
  location?: string;
  experience_years?: number;
  current_position?: string;
  current_company?: string;
  education?: string;
  skills?: string[];
  certifications?: string[];
  languages?: string[];
  salary_expectation?: number;
  availability?: string;
  work_experience?: any[];
}

export interface AnalysisResults {
  form_data?: {
    name?: string;
    email?: string;
    phone?: string;
    location?: string;
    experience_years?: number;
    current_position?: string;
    current_company?: string;
    education?: string;
    skills?: string[];
    certifications?: string[];
    languages?: string[];
    salary_expectation?: number;
    availability?: string;
    work_experience?: any[];
  };
  analysis?: {
    overall_fitment_score?: number;
    strengths?: string[];
    concerns?: string[];
  };
}

export interface AutoFillOptions {
  onFieldsFilled?: (count: number, total: number) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export const useEnhancedAutoFill = (
  setValue: UseFormSetValue<CandidateFormData>,
  trigger: UseFormTrigger<CandidateFormData>,
  setAutoFilledFields: React.Dispatch<React.SetStateAction<Set<string>>>
) => {
  const autoFillFormFromAnalysis = useCallback(async (
    analysisResults: AnalysisResults,
    parsedFields: any,
    options: AutoFillOptions = {}
  ) => {
    try {
      console.log('🔄 Starting enhanced auto-fill from AI analysis results');
      console.log('📊 Analysis results:', analysisResults);
      console.log('📋 Parsed fields:', parsedFields);
      
      let fieldsFilled = 0;
      const totalFields = 14; // Total number of fields we're trying to fill
      
      // Helper function to safely set form values
      const safeSetValue = (fieldName: keyof CandidateFormData, value: any, transform?: (val: any) => any) => {
        if (value !== null && value !== undefined && value !== '') {
          const transformedValue = transform ? transform(value) : value;
          setValue(fieldName, transformedValue);
          fieldsFilled++;
          setAutoFilledFields((prev: Set<string>) => new Set([...prev, fieldName as string]));
          console.log(`✅ Auto-filled ${fieldName}:`, transformedValue);
        }
      };
      
      // Helper function to extract years from experience
      const extractExperienceYears = (experience: any): number | undefined => {
        if (typeof experience === 'number') return experience;
        if (typeof experience === 'string') {
          const match = experience.match(/(\d+)/);
          return match ? parseInt(match[1]) : undefined;
        }
        return undefined;
      };
      
      // Helper function to format work experience
      const formatWorkExperience = (workExp: any[]): any[] => {
        if (!Array.isArray(workExp)) return [];
        return workExp.map(exp => ({
          company: exp.company || exp.employer || '',
          position: exp.position || exp.title || exp.role || '',
          duration: exp.duration || exp.period || exp.years || '',
          description: exp.description || exp.responsibilities || ''
        }));
      };

      // Priority 1: Use AI analysis results (most comprehensive)
      if (analysisResults.form_data) {
        console.log('🎯 Using AI analysis results for auto-fill');
        const formData = analysisResults.form_data;
        
        // Basic information
        safeSetValue('name', formData.name);
        safeSetValue('email', formData.email);
        safeSetValue('phone', formData.phone);
        safeSetValue('location', formData.location);
        
        // Professional information
        safeSetValue('experience_years', formData.experience_years, extractExperienceYears);
        safeSetValue('current_position', formData.current_position);
        safeSetValue('current_company', formData.current_company);
        safeSetValue('education', formData.education);
        
        // Skills and certifications
        safeSetValue('skills', formData.skills);
        safeSetValue('certifications', formData.certifications);
        safeSetValue('languages', formData.languages);
        
        // Additional information
        safeSetValue('salary_expectation', formData.salary_expectation);
        safeSetValue('availability', formData.availability);
        safeSetValue('work_experience', formData.work_experience, formatWorkExperience);
      }
      
      // Priority 2: Fallback to parsed fields (basic extraction)
      else if (parsedFields) {
        console.log('📋 Using parsed fields for auto-fill (fallback)');
        
        // Basic information
        const personalInfo = parsedFields.personal_info || {};
        safeSetValue('name', personalInfo.name || parsedFields.name || parsedFields.full_name || parsedFields.candidate_name);
        safeSetValue('email', personalInfo.email || parsedFields.email || parsedFields.email_address);
        safeSetValue('phone', personalInfo.phone || parsedFields.phone || parsedFields.phone_number || parsedFields.contact_number);
        safeSetValue('location', personalInfo.location || parsedFields.location || parsedFields.address || parsedFields.city);
        
        // Professional information
        const experienceYears = parsedFields.experience_years || parsedFields.years_of_experience;
        const numericExperience = experienceYears ? Number(experienceYears) : undefined;
        safeSetValue('experience_years', numericExperience, extractExperienceYears);
        safeSetValue('current_position', parsedFields.current_position || parsedFields.current_role || parsedFields.job_title);
        safeSetValue('current_company', parsedFields.current_company || parsedFields.current_employer || parsedFields.company);
        safeSetValue('education', parsedFields.education || parsedFields.educational_background || parsedFields.degree);
        
        // Skills and certifications
        safeSetValue('skills', parsedFields.skills || parsedFields.technical_skills || parsedFields.programming_skills);
        safeSetValue('certifications', parsedFields.certifications || parsedFields.certificates);
        safeSetValue('languages', parsedFields.languages || parsedFields.programming_languages);
        
        // Additional information
        const additionalInfo = parsedFields.additional_info || {};
        const salaryExpectation = additionalInfo.salary_expectation || parsedFields.salary_expectation || parsedFields.expected_salary || parsedFields.salary;
        const numericSalary = salaryExpectation ? Number(salaryExpectation) : undefined;
        safeSetValue('salary_expectation', numericSalary);
        safeSetValue('availability', additionalInfo.availability || parsedFields.availability || parsedFields.notice_period || parsedFields.start_date);
        
        // Work experience
        safeSetValue('work_experience', parsedFields.work_experience || parsedFields.experience || parsedFields.employment_history, formatWorkExperience);
      }
      
      // Calculate fill percentage
      const fillPercentage = Math.round((fieldsFilled / totalFields) * 100);
      
      console.log(`📊 Enhanced auto-fill completed: ${fieldsFilled}/${totalFields} fields filled (${fillPercentage}%)`);
      
      // Show success message with details
      if (fieldsFilled > 0) {
        const message = analysisResults.form_data 
          ? `Form auto-filled with AI-enhanced data! ${fieldsFilled} fields populated with comprehensive analysis.`
          : `Form auto-filled from CV! ${fieldsFilled} fields populated. Please review and edit as needed.`;
        
        toast.success(message);
        
        // Trigger form validation to enable submit button
        await trigger();
        
        // Call completion callback
        options.onComplete?.();
        
        // Call fields filled callback
        options.onFieldsFilled?.(fieldsFilled, totalFields);
        
      } else {
        toast.error('CV was processed but no fields could be auto-filled. Please fill the form manually.');
        options.onError?.(new Error('No fields could be auto-filled'));
      }
      
      console.log('✅ Enhanced auto-fill process completed successfully');
      
    } catch (error) {
      console.error('❌ Failed to auto-fill form:', error);
      toast.error('Failed to auto-fill form. Please fill manually.');
      options.onError?.(error as Error);
    }
  }, [setValue, trigger, setAutoFilledFields]);

  return {
    autoFillFormFromAnalysis,
  };
};
